import React, { useContext, useEffect, useState } from "react";
import { Table, Button, Tag, Typography, DatePicker } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import styles from "./my-applications.module.css";
import Text from "../../context/language-context";
import { FullscreenModal } from "../../components/fullscreen-modal";
import useFetch from "../../hooks/useFetch";
import {
  eResultCode,
  eUSER_FORM_TYPE,
  eFORMSTATUS,
  eSTAGE,
  eFORM_FOR,
} from "../../utils/enum";
import { LoaderContext } from "../../context/loader";
import EncryptUtils from "../../utils/encrypt";
import dayjs from "dayjs";

// Import archived 2025 form components for preview
import TeacherPartOne2025 from "../forms/archive/2025/teacher/teacher_pt_1_2025";
import TeacherPartTwo2025 from "../forms/archive/2025/teacher/teacher_pt_2_2025";
import StudentPartOne2025 from "../forms/archive/2025/student/student_pt_1_2025";
import StudentPartTwo2025 from "../forms/archive/2025/student/student_pt_2_2025";
import SchoolPartOne2025 from "../forms/archive/2025/school/school_pt_1_2025";
import SchoolPartTwo2025 from "../forms/archive/2025/school/school_pt_2_2025";

// Import current 2026 form components for preview
import TeacherPartOne2026 from "../forms/current/teacher/teacher_pt_1_2026";
import TeacherPartTwo2026 from "../forms/current/teacher/teacher_pt_2_2026";
import StudentPartOne2026 from "../forms/current/student/student_pt_1_2026";
import StudentPartTwo2026 from "../forms/current/student/student_pt_2_2026";
import SchoolPartOne2026 from "../forms/current/school/school_pt_1_2026";
import SchoolPartTwo2026 from "../forms/current/school/school_pt_2_2026";

import { GetUserForms } from "../../utils/api.constant";
import { CURRENT_YEAR } from "../../utils/constants";
import { TPreviewState } from "../../types";

const { Title } = Typography;

interface ApplicationData {
  id?: number; // For 2026 response
  userFormMappingId?: number; // For 2025 response
  userName: string;
  userFormType: number;
  currentStage: number;
  formFor: number;
  userId?: number;
  year?: string;
  formCode?: string;
  formIds?: {
    stage1: number | null;
    stage2: number | null;
  };
}

const MyApplications = () => {
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  // Consolidated preview modal state
  const [previewData, setPreviewData] = useState<TPreviewState | null>(null);

  const { post } = useFetch();
  const contextForLoader = useContext(LoaderContext);
  const { setIsLoading } = contextForLoader as any;

  const pageTitle = Text({ tid: "MY_APPLICATIONS", def: "My Applications" });
  const previewText = Text({ tid: "PREVIEW", def: "Preview" });
  const noDataText = Text({
    tid: "NO_APPLICATIONS_FOUND",
    def: "No previous applications found",
  });

  const getApplications = async (year: number) => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          year: year,
        },
      };
      const response = await post(GetUserForms, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        // Transform 2026 data structure to match expected format
        const transformedData =
          data?.map((item: any) => {
            // For 2026, we get flat structure with id field
            if (item.id && !item.formIds) {
              return {
                ...item,
                userFormMappingId: item.id,
                formIds: {
                  stage1:
                    item.currentStage >= eFORMSTATUS.STAGE1INPROGRESS
                      ? item.id
                      : null,
                  stage2:
                    item.currentStage >= eFORMSTATUS.STAGE1APPROVED
                      ? item.id
                      : null,
                },
              };
            }
            // For 2025, data already has formIds structure
            return item;
          }) || [];
        setApplications(transformedData);
      } else if (returnCode === eResultCode.NO_DATA_FOUND) {
        setApplications([]);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getApplications(selectedYear);
  }, [selectedYear]);

  const getFormTypeName = (formType: number) => {
    switch (formType) {
      case eUSER_FORM_TYPE.STUDENT:
        return Text({ tid: "STUDENT", def: "Student" });
      case eUSER_FORM_TYPE.TEACHER:
        return Text({ tid: "TEACHER", def: "Teacher" });
      case eUSER_FORM_TYPE.SCHOOL:
        return Text({ tid: "SCHOOL", def: "School" });
      default:
        return "-";
    }
  };

  const getFormForName = (formFor: number) => {
    switch (formFor) {
      case eFORM_FOR.YOURSELF:
        return Text({ tid: "yourself", def: "Yourself" });
      case eFORM_FOR.OTHER:
        return Text({ tid: "others", def: "For Someone Else" });
      default:
        return "-";
    }
  };

  const getStatusTag = (stage: number) => {
    if (stage >= eFORMSTATUS.STAGE2SELECTED) {
      return (
        <Tag color='green'>{Text({ tid: "SELECTED", def: "Selected" })}</Tag>
      );
    } else if (stage >= eFORMSTATUS.STAGE2APPROVED) {
      return (
        <Tag color='blue'>
          {Text({ tid: "STAGE_2_APPROVED", def: "Stage 2 Approved" })}
        </Tag>
      );
    } else if (stage >= eFORMSTATUS.STAGE2SUBMITTED) {
      return (
        <Tag color='processing'>
          {Text({ tid: "STAGE_2_SUBMITTED", def: "Stage 2 Submitted" })}
        </Tag>
      );
    } else if (stage >= eFORMSTATUS.STAGE1APPROVED) {
      return (
        <Tag color='cyan'>
          {Text({ tid: "STAGE_1_APPROVED", def: "Stage 1 Approved" })}
        </Tag>
      );
    } else if (stage >= eFORMSTATUS.STAGE1SUBMITTED) {
      return (
        <Tag color='orange'>
          {Text({ tid: "STAGE_1_SUBMITTED", def: "Stage 1 Submitted" })}
        </Tag>
      );
    } else if (stage === eFORMSTATUS.STAGE1REJECTED) {
      return (
        <Tag color='red'>{Text({ tid: "REJECTED", def: "Rejected" })}</Tag>
      );
    } else {
      return (
        <Tag color='default'>
          {Text({ tid: "IN_PROGRESS", def: "In Progress" })}
        </Tag>
      );
    }
  };

  const handlePreview = (
    stage: number,
    userType: number,
    ufmId: number,
    formId: number,
    userId: number,
    formFor: number,
  ) => {
    setPreviewData({
      formId: EncryptUtils.encrypt(formId.toString()),
      stage,
      userType,
      ufmId: EncryptUtils.encrypt(ufmId.toString()),
      userId: EncryptUtils.encrypt(userId.toString()),
      formFor: EncryptUtils.encrypt(formFor.toString()),
    });
  };

  const renderPreviewContent = () => {
    if (!previewData) return null;

    const is2025 = selectedYear === 2025;
    const isStage1 = previewData.stage === eSTAGE.STAGE1;

    // For 2025: pass both formId and ufmId
    // For current year: pass formId, userId, formFor
    const formProps = is2025
      ? {
          disabled: true,
          currentFormId: previewData.formId,
          currentUfmId: previewData.ufmId,
        }
      : {
          disabled: true,
          currentFormId: previewData.formId,
          currentUserId: previewData.userId,
          currentFormFor: previewData.formFor,
        };

    if (previewData.userType === eUSER_FORM_TYPE.STUDENT) {
      if (isStage1) {
        return is2025 ? (
          <StudentPartOne2025 {...formProps} />
        ) : (
          <StudentPartOne2026 {...formProps} />
        );
      } else {
        return is2025 ? (
          <StudentPartTwo2025 {...formProps} />
        ) : (
          <StudentPartTwo2026 {...formProps} />
        );
      }
    } else if (previewData.userType === eUSER_FORM_TYPE.TEACHER) {
      if (isStage1) {
        return is2025 ? (
          <TeacherPartOne2025 {...formProps} />
        ) : (
          <TeacherPartOne2026 {...formProps} />
        );
      } else {
        return is2025 ? (
          <TeacherPartTwo2025 {...formProps} />
        ) : (
          <TeacherPartTwo2026 {...formProps} />
        );
      }
    } else if (previewData.userType === eUSER_FORM_TYPE.SCHOOL) {
      if (isStage1) {
        return is2025 ? (
          <SchoolPartOne2025 {...formProps} />
        ) : (
          <SchoolPartOne2026 {...formProps} />
        );
      } else {
        return is2025 ? (
          <SchoolPartTwo2025 {...formProps} />
        ) : (
          <SchoolPartTwo2026 {...formProps} />
        );
      }
    }

    return null;
  };

  const handleYearChange = (date: any) => {
    if (date) {
      const year = date.year();
      setSelectedYear(year);
    }
  };

  const columns = [
    {
      title: Text({ tid: "APPLICANT_NAME", def: "Applicant Name" }),
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: Text({ tid: "APPLICATION_TYPE", def: "Application Type" }),
      dataIndex: "userFormType",
      key: "userFormType",
      render: (type: number) => getFormTypeName(type),
      filters: [
        {
          text: Text({ tid: "STUDENT", def: "Student" }),
          value: eUSER_FORM_TYPE.STUDENT,
        },
        {
          text: Text({ tid: "TEACHER", def: "Teacher" }),
          value: eUSER_FORM_TYPE.TEACHER,
        },
        {
          text: Text({ tid: "SCHOOL", def: "School" }),
          value: eUSER_FORM_TYPE.SCHOOL,
        },
      ],
      onFilter: (value: any, record: ApplicationData) =>
        record.userFormType === value,
    },
    {
      title: Text({ tid: "FORM_FOR", def: "Form For" }),
      dataIndex: "formFor",
      key: "formFor",
      render: (formFor: number) => getFormForName(formFor),
      filters: [
        {
          text: Text({ tid: "yourself", def: "Yourself" }),
          value: eFORM_FOR.YOURSELF,
        },
        {
          text: Text({ tid: "others", def: "For Someone Else" }),
          value: eFORM_FOR.OTHER,
        },
      ],
      onFilter: (value: any, record: ApplicationData) =>
        record.formFor === value,
    },
    {
      title: Text({ tid: "YEAR", def: "Year" }),
      key: "year",
      render: () => selectedYear,
    },
    {
      title: Text({ tid: "STATUS", def: "Status" }),
      dataIndex: "currentStage",
      key: "currentStage",
      render: (stage: number) => getStatusTag(stage),
    },
    {
      title: Text({ tid: "ACTIONS", def: "Actions" }),
      key: "actions",
      render: (_: any, record: ApplicationData) => {
        const ufmId = record.userFormMappingId || record.id || 0;
        return (
          <div className={styles.actionButtons}>
            {record.formIds?.stage1 && (
              <Button
                type='link'
                icon={<EyeOutlined />}
                onClick={() =>
                  handlePreview(
                    eSTAGE.STAGE1,
                    record.userFormType,
                    ufmId,
                    record.formIds!.stage1 as number,
                    record.userId || 0,
                    record.formFor,
                  )
                }
              >
                {Text({ tid: "PREVIEW_STAGE_1", def: "Preview Stage 1" })}
              </Button>
            )}
            {record.formIds?.stage2 &&
              record.currentStage >= eFORMSTATUS.STAGE1APPROVED && (
                <Button
                  type='link'
                  icon={<EyeOutlined />}
                  onClick={() =>
                    handlePreview(
                      eSTAGE.STAGE2,
                      record.userFormType,
                      ufmId,
                      record.formIds!.stage2 as number,
                      record.userId || 0,
                      record.formFor,
                    )
                  }
                >
                  {Text({ tid: "PREVIEW_STAGE_2", def: "Preview Stage 2" })}
                </Button>
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title
          level={3}
          className={styles.pageTitle}
        >
          {pageTitle}
        </Title>
        <div className={styles.filterSection}>
          <span className={styles.filterLabel}>
            {Text({ tid: "SELECT_YEAR", def: "Select Year" })}:
          </span>
          <DatePicker
            picker='year'
            value={dayjs().year(selectedYear)}
            onChange={handleYearChange}
            disabledDate={(current) => {
              // Disable future years and years before 2025
              return (
                current &&
                (current.year() > CURRENT_YEAR || current.year() < 2025)
              );
            }}
            style={{ width: 120 }}
          />
        </div>
      </div>

      <Table
        dataSource={applications}
        columns={columns}
        rowKey={(record) => record.userFormMappingId || record.id || 0}
        locale={{ emptyText: noDataText }}
        pagination={{ pageSize: 10 }}
      />

      <FullscreenModal
        open={!!previewData}
        onClose={() => setPreviewData(null)}
      >
        {renderPreviewContent()}
      </FullscreenModal>
    </div>
  );
};

export default MyApplications;
